<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show', 'chatCreated']);

const show = computed({
  get: () => props.show,
  set: val => emit('update:show', val),
});

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const name = ref('');
const description = ref('');
const agentSearch = ref('');
const selectedIds = ref([]);
const isCreating = ref(false);
const avatarFile = ref(null);
const avatarPreview = ref('');
const fileInputRef = ref(null);

let debounceTimer = null;

// [polish] clear debounce timer on unmount
onBeforeUnmount(() => clearTimeout(debounceTimer));

const onAvatarChange = e => {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) return;
  avatarFile.value = file;
  avatarPreview.value = URL.createObjectURL(file);
};

const removeAvatar = () => {
  avatarFile.value = null;
  if (avatarPreview.value) URL.revokeObjectURL(avatarPreview.value);
  avatarPreview.value = '';
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const agents = computed(() => store.getters['internalChats/getAgentsCache']);

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase();
  if (!q) return agents.value;
  return agents.value.filter(a => a.name.toLowerCase().includes(q));
});

const canCreate = computed(
  () => name.value.trim().length > 0 && selectedIds.value.length > 0
);

const onAgentSearch = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    store.dispatch('internalChats/fetchAgents', agentSearch.value);
  }, 300);
};

const submit = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;
  try {
    const chat = await store.dispatch('internalChats/createGroup', {
      name: name.value.trim(),
      description: description.value.trim(),
      memberIds: selectedIds.value,
      avatar: avatarFile.value,
    });
    emit('chatCreated', chat);
    show.value = false;
  } catch {
    // [polish] surface error rather than silently discarding
    showAlert(t('INTERNAL_CHATS.TOAST.ERROR_GENERIC'));
  } finally {
    isCreating.value = false;
  }
};

const onClose = () => {
  name.value = '';
  description.value = '';
  agentSearch.value = '';
  selectedIds.value = [];
  removeAvatar();
};

watch(
  () => props.show,
  val => {
    if (val) {
      store.dispatch('internalChats/fetchAgents', '');
    }
  }
);
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="p-6 flex flex-col gap-4">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHATS.NEW_GROUP_TITLE') }}
      </h3>

      <!-- Avatar upload -->
      <div class="flex items-center gap-3">
        <div
          class="size-16 rounded-full border border-n-weak bg-n-alpha-1 flex items-center justify-center overflow-hidden flex-shrink-0"
        >
          <img
            v-if="avatarPreview"
            :src="avatarPreview"
            class="size-full object-cover"
            alt=""
          />
          <span v-else class="i-lucide-users size-6 text-n-slate-9" />
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="cursor-pointer text-xs font-medium text-n-brand hover:text-n-brand/80"
          >
            {{ t('INTERNAL_CHATS.UPLOAD_GROUP_AVATAR') }}
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onAvatarChange"
            />
          </label>
          <button
            v-if="avatarPreview"
            type="button"
            class="text-xs text-n-slate-9 hover:text-n-red-9 text-left"
            @click="removeAvatar"
          >
            {{ t('INTERNAL_CHATS.REMOVE_AVATAR') }}
          </button>
        </div>
      </div>

      <!-- [polish] label text should use GROUP_NAME_LABEL, not GROUP_NAME_PLACEHOLDER reused as label -->
      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-medium text-n-slate-11"
          for="group-name-input"
        >
          {{ t('INTERNAL_CHATS.GROUP_NAME_LABEL') }}
        </label>
        <input
          id="group-name-input"
          v-model="name"
          type="text"
          maxlength="80"
          :placeholder="t('INTERNAL_CHATS.GROUP_NAME_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand placeholder:text-n-slate-9"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-medium text-n-slate-11"
          for="group-description-input"
        >
          {{ t('INTERNAL_CHATS.GROUP_DESCRIPTION_LABEL') }}
        </label>
        <textarea
          id="group-description-input"
          v-model="description"
          maxlength="280"
          :placeholder="t('INTERNAL_CHATS.GROUP_DESCRIPTION_PLACEHOLDER')"
          rows="2"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand placeholder:text-n-slate-9 resize-none"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('INTERNAL_CHATS.MEMBERS_LABEL') }}
        </label>
        <input
          v-model="agentSearch"
          type="text"
          :placeholder="t('INTERNAL_CHATS.SEARCH_AGENT')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand placeholder:text-n-slate-9"
          @input="onAgentSearch"
        />
        <div class="flex flex-col gap-1 max-h-48 overflow-y-auto">
          <label
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          >
            <input
              v-model="selectedIds"
              type="checkbox"
              :value="agent.id"
              class="rounded accent-n-brand"
            />
            <Avatar
              :name="agent.name"
              :src="agent.avatar_url"
              :size="28"
              rounded-full
            />
            <span class="text-sm text-n-slate-12">{{ agent.name }}</span>
          </label>
          <p
            v-if="filteredAgents.length === 0"
            class="py-2 text-center text-sm text-n-slate-9"
          >
            {{ t('INTERNAL_CHATS.EMPTY_LIST') }}
          </p>
        </div>
      </div>

      <button
        :disabled="!canCreate || isCreating"
        class="w-full rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center gap-2"
        @click="submit"
      >
        <!-- [polish] spinner while creating -->
        <span
          v-if="isCreating"
          class="i-lucide-loader-2 size-3.5 animate-spin"
        />
        {{ t('INTERNAL_CHATS.CREATE_GROUP') }}
      </button>
    </div>
  </Modal>
</template>
